import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useContributions from "./useContributions";
import useStarred from "./useStarred";
import {
  CAL_COL_PITCH,
  CAL_GUTTER,
  CAL_INSET,
  CAL_LABEL_X,
  CAL_RIGHT,
  CENTER,
  DUST_COLOR,
  EMPTY_COLOR,
  REPO_COLORS,
  STAR_COLORS,
  VIEW_H_CALENDAR,
  VIEW_H_GRAPH,
  VIEW_W,
  armPath,
  calendarCell,
  calendarPos,
  calendarRowY,
  clamp,
  clusterLabelY,
  easeInOut,
  graphPos,
  languageCenters,
  lerp,
  mixHex,
  monthCenter,
  repoLevel,
  repoPos,
  repoRadius,
  starRadius,
} from "./geometry";
import "./GithubActivity.css";

const MIN_SCALE = 0.6;
const MAX_SCALE = 2.4;
const HIT_GRAPH = 16;
const HIT_REPO_MIN = 8;
const MORPH_MS = 900;
// Fraction of the crossfade spent staggering: the clusters nearest the centre
// of the arm ignite first and the outer ones follow, so the sky resolves along
// the spiral rather than switching on all at once.
const CROSS_SPREAD = 0.35;
// A pointer has to travel this far before it counts as a drag rather than a
// click. Without the threshold, capturing on pointerdown would swallow the
// click on a repo.
const DRAG_SLOP = 3;
const IDENTITY = { x: 0, y: 0, scale: 1 };
const ARM = armPath();

const dayFormat = new Intl.DateTimeFormat("en", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

function formatDay(date) {
  return dayFormat.format(new Date(`${date}T00:00:00Z`));
}

function plural(count, word) {
  return `${word}${count === 1 ? "" : "s"}`;
}

// 365428 -> 365k, 5710 -> 5.7k, 942 -> 942.
function compactCount(count) {
  if (count < 1000) return String(count);
  if (count < 10000) return `${(count / 1000).toFixed(1)}k`;
  return `${Math.round(count / 1000)}k`;
}

function hasMoved(view) {
  return (
    Math.abs(view.x) > 0.5 ||
    Math.abs(view.y) > 0.5 ||
    Math.abs(view.scale - 1) > 0.01
  );
}

// Per-element progress through the staggered crossfade.
function stagger(progress, delay) {
  return clamp((progress - delay * CROSS_SPREAD) / (1 - CROSS_SPREAD), 0, 1);
}

const icons = {
  github: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M12 .5a11.5 11.5 0 0 0-3.64 22.41c.58.11.79-.25.79-.56v-2c-3.2.7-3.88-1.54-3.88-1.54-.53-1.34-1.29-1.7-1.29-1.7-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.7 1.26 3.36.96.1-.75.4-1.26.73-1.55-2.56-.29-5.25-1.28-5.25-5.7 0-1.26.45-2.29 1.19-3.1-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.8 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.12 3.05.74.81 1.18 1.84 1.18 3.1 0 4.43-2.69 5.4-5.26 5.69.41.36.78 1.06.78 2.14v3.17c0 .31.21.68.8.56A11.5 11.5 0 0 0 12 .5Z" />
    </svg>
  ),
  spiral: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <path d="M12 12a2 2 0 0 1 2-2 4 4 0 0 1 4 4 6 6 0 0 1-6 6 8 8 0 0 1-8-8 10 10 0 0 1 10-10" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <circle cx="8.5" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3.4 14.09 9.4 20.45 9.53 15.37 13.37 17.24 19.45 12 15.8 6.76 19.45 8.63 13.37 3.55 9.53 9.91 9.4Z" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  minus: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M5 12h14" />
    </svg>
  ),
  recenter: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  ),
};

const GithubActivity = ({ username = "carlosdtrejo" }) => {
  const { status, days, total } = useContributions(username);
  const [mode, setMode] = useState("graph");
  // The starred list costs a second API call, so it is only requested once the
  // reader actually opens that view. Once armed it stays armed.
  const [starredArmed, setStarredArmed] = useState(false);
  const starred = useStarred(username, starredArmed);
  const [hovered, setHovered] = useState(null);
  const [hoveredRepo, setHoveredRepo] = useState(null);
  const [moved, setMoved] = useState(false);
  const [dragging, setDragging] = useState(false);

  const stageRef = useRef(null);
  const svgRef = useRef(null);
  const dustRef = useRef(null);
  const dustFillRef = useRef(null);
  const worldRef = useRef(null);
  const armRef = useRef(null);
  const linksRef = useRef(null);
  const daysLayerRef = useRef(null);
  const graphLabelsRef = useRef(null);
  const calLayerRef = useRef(null);
  const starRefs = useRef([]);
  const haloRefs = useRef([]);
  const hitRefs = useRef([]);
  const repoRefs = useRef([]);
  const repoHaloRefs = useRef([]);
  const repoHitRefs = useRef([]);
  const repoLabelRefs = useRef([]);

  const layoutRef = useRef(null);
  const starLayoutRef = useRef(null);
  const modeRef = useRef(mode);
  const reducedRef = useRef(false);
  const dragRef = useRef(null);
  const rafRef = useRef(0);
  const morphRef = useRef(0);
  const morphTargetRef = useRef(0);
  const morphFromRef = useRef(0);
  const crossRef = useRef(0);
  const crossTargetRef = useRef(0);
  const crossFromRef = useRef(0);
  const tweenStartRef = useRef(0);
  const viewRef = useRef(IDENTITY);
  const viewTargetRef = useRef(IDENTITY);

  // Positions for both layouts, computed once per dataset. Each day exists in
  // both, so the morph is one-to-one and nothing is created or destroyed.
  const layout = useMemo(() => {
    if (!days.length) return null;

    const order = new Map();
    const months = [];
    days.forEach((day) => {
      if (!order.has(day.monthKey)) {
        order.set(day.monthKey, months.length);
        months.push({
          key: day.monthKey,
          label: day.monthLabel,
        });
      }
    });

    const monthCount = months.length;
    const stars = days.map((day, index) => {
      const placed = { ...day, monthIndex: order.get(day.monthKey) };
      return {
        ...placed,
        index,
        g: graphPos(placed, monthCount),
        c: calendarPos(placed),
        r: starRadius(placed.level),
        cell: calendarCell(placed.level),
      };
    });

    const active = stars.filter((star) => star.count > 0);

    return {
      stars,
      active,
      clusters: months.map((month, index) => ({
        ...month,
        ...monthCenter(index, monthCount),
      })),
      rows: months.map((month, index) => ({
        key: month.key,
        label: month.label,
        y: calendarRowY(index),
      })),
      // A faint line through the active days, in date order. With a sparse
      // year this reads better than it would with a dense one.
      linkPath:
        active.length > 1
          ? `M${active.map((s) => `${s.g.x.toFixed(1)} ${s.g.y.toFixed(1)}`).join("L")}`
          : "",
    };
  }, [days]);

  // The same arm, read a second way: one cluster per language rather than per
  // month, ordered biggest first, each repo sized and toned by its star count.
  const starLayout = useMemo(() => {
    if (!starred.repos.length) return null;

    const groups = new Map();
    starred.repos.forEach((repo) => {
      const key = repo.language || "Other";
      if (!groups.has(key)) groups.set(key, { key, label: key, repos: [] });
      groups.get(key).repos.push(repo);
    });

    const ordered = [...groups.values()].sort(
      (a, b) => b.repos.length - a.repos.length || a.key.localeCompare(b.key)
    );
    const placed = languageCenters(ordered);
    const spread = Math.max(placed.length - 1, 1);

    const stars = [];
    const clusters = placed.map((cluster, clusterIndex) => {
      const delay = clusterIndex / spread;
      // Most-starred first, so the brightest repo anchors the cluster centre.
      [...cluster.repos]
        .sort((a, b) => b.stars - a.stars || a.fullName.localeCompare(b.fullName))
        .forEach((repo, seat, seated) => {
          const point = repoPos(cluster, seat, seated.length, repo.fullName);
          stars.push({
            ...repo,
            index: stars.length,
            level: repoLevel(repo.stars),
            r: repoRadius(repo.stars),
            // The most-starred repo in each language anchors its cluster and is
            // the only one that gets a halo. Haloing every popular repo instead
            // stacked three translucent discs into a smudge.
            anchor: seat === 0,
            cx: cluster.x,
            cy: cluster.y,
            x: point.x,
            y: point.y,
            delay,
          });
        });

      return {
        key: cluster.key,
        label: cluster.label,
        count: cluster.repos.length,
        x: cluster.x,
        labelY: clusterLabelY(cluster),
        delay,
      };
    });

    return {
      stars,
      clusters,
      anchors: stars.filter((repo) => repo.anchor),
      total: starred.repos.length,
      languages: clusters.length,
    };
  }, [starred.repos]);

  // Writes the current frame straight to the DOM. Re-rendering ~370 rects
  // through React every frame would be far too much churn.
  const draw = useCallback(() => {
    const current = layoutRef.current;
    const m = morphRef.current;
    const c = crossRef.current;
    const view = viewRef.current;

    if (worldRef.current) {
      worldRef.current.setAttribute(
        "transform",
        `translate(${view.x.toFixed(2)} ${view.y.toFixed(2)}) translate(${CENTER.x} ${CENTER.y}) scale(${view.scale.toFixed(4)}) translate(${-CENTER.x} ${-CENTER.y})`
      );
    }

    // The calendar is framed shorter than the constellation.
    if (svgRef.current) {
      const height = lerp(VIEW_H_GRAPH, VIEW_H_CALENDAR, m);
      svgRef.current.setAttribute("viewBox", `0 0 ${VIEW_W} ${height.toFixed(2)}`);
    }

    // The arm is the one thing both readings share, so it never crossfades --
    // only the calendar folds it away.
    const graphOpacity = 1 - m;
    if (armRef.current) {
      armRef.current.setAttribute("opacity", graphOpacity.toFixed(3));
    }
    if (linksRef.current) {
      linksRef.current.setAttribute("opacity", (graphOpacity * (1 - c)).toFixed(3));
    }
    if (graphLabelsRef.current) {
      graphLabelsRef.current.setAttribute("opacity", (graphOpacity * (1 - c)).toFixed(3));
    }
    if (calLayerRef.current) {
      calLayerRef.current.setAttribute("opacity", m.toFixed(3));
    }
    if (daysLayerRef.current) {
      daysLayerRef.current.setAttribute("opacity", (1 - c).toFixed(3));
    }

    if (current) {
      current.stars.forEach((star, index) => {
        const node = starRefs.current[index];
        if (!node) return;
        // Graph: a circle of radius r. Calendar: a flat pill if the day is
        // empty, otherwise a circle sized by level.
        const w = lerp(star.r * 2, star.cell.w, m);
        const h = lerp(star.r * 2, star.cell.h, m);
        node.setAttribute("x", (lerp(star.g.x, star.c.x, m) - w / 2).toFixed(2));
        node.setAttribute("y", (lerp(star.g.y, star.c.y, m) - h / 2).toFixed(2));
        node.setAttribute("width", w.toFixed(2));
        node.setAttribute("height", h.toFixed(2));
        node.setAttribute("rx", lerp(star.r, star.cell.rx, m).toFixed(2));
      });

      current.active.forEach((star, index) => {
        const x = lerp(star.g.x, star.c.x, m);
        const y = lerp(star.g.y, star.c.y, m);

        const halo = haloRefs.current[index];
        if (halo) {
          const size = star.r * 6.4;
          halo.setAttribute("x", (x - size / 2).toFixed(2));
          halo.setAttribute("y", (y - size / 2).toFixed(2));
          halo.setAttribute("width", size.toFixed(2));
          halo.setAttribute("height", size.toFixed(2));
          halo.setAttribute("rx", (size / 2).toFixed(2));
          halo.setAttribute("opacity", (0.3 * (1 - m)).toFixed(3));
        }

        const hit = hitRefs.current[index];
        if (hit) {
          const hw = lerp(HIT_GRAPH, CAL_COL_PITCH, m);
          const hh = lerp(HIT_GRAPH, 20, m);
          hit.setAttribute("x", (x - hw / 2).toFixed(2));
          hit.setAttribute("y", (y - hh / 2).toFixed(2));
          hit.setAttribute("width", hw.toFixed(2));
          hit.setAttribute("height", hh.toFixed(2));
        }
      });

      // Empty days are the one colour that differs between layouts. They share
      // a group, so this is a single write rather than one per element.
      if (dustRef.current) {
        const dustFill = mixHex(DUST_COLOR, EMPTY_COLOR, m);
        if (dustFill !== dustFillRef.current) {
          dustRef.current.setAttribute("fill", dustFill);
          dustFillRef.current = dustFill;
        }
      }
    }

    const sky = starLayoutRef.current;
    if (sky) {
      sky.stars.forEach((repo, index) => {
        const node = repoRefs.current[index];
        if (!node) return;
        // Each repo ignites at its language's centre and settles into its
        // seat, so the cluster reads as a group before it reads as points.
        const t = stagger(c, repo.delay);
        const x = lerp(repo.cx, repo.x, t);
        const y = lerp(repo.cy, repo.y, t);
        node.setAttribute("cx", x.toFixed(2));
        node.setAttribute("cy", y.toFixed(2));
        node.setAttribute("r", (repo.r * (0.5 + 0.5 * t)).toFixed(2));
        node.setAttribute("opacity", t.toFixed(3));

        const hit = repoHitRefs.current[index];
        if (hit) {
          hit.setAttribute("cx", x.toFixed(2));
          hit.setAttribute("cy", y.toFixed(2));
          hit.setAttribute("r", Math.max(repo.r + 3.5, HIT_REPO_MIN).toFixed(2));
        }
      });

      sky.anchors.forEach((repo, index) => {
        const halo = repoHaloRefs.current[index];
        if (!halo) return;
        const t = stagger(c, repo.delay);
        halo.setAttribute("cx", lerp(repo.cx, repo.x, t).toFixed(2));
        halo.setAttribute("cy", lerp(repo.cy, repo.y, t).toFixed(2));
        halo.setAttribute("r", (repo.r * 3.4).toFixed(2));
        halo.setAttribute("opacity", (0.13 * t).toFixed(3));
      });

      sky.clusters.forEach((cluster, index) => {
        const label = repoLabelRefs.current[index];
        if (label) label.setAttribute("opacity", stagger(c, cluster.delay).toFixed(3));
      });
    }
  }, []);

  const animate = useCallback(() => {
    const step = (now) => {
      const morphTarget = morphTargetRef.current;
      const crossTarget = crossTargetRef.current;
      const viewTarget = viewTargetRef.current;
      const view = viewRef.current;

      // The morph and the crossfade are the signature moves, so they share one
      // fixed, deliberate duration. Pan and zoom stay on exponential smoothing
      // below, which keeps them feeling attached to the pointer.
      if (morphRef.current !== morphTarget || crossRef.current !== crossTarget) {
        const progress = clamp((now - tweenStartRef.current) / MORPH_MS, 0, 1);
        const eased = easeInOut(progress);
        morphRef.current =
          progress >= 1 ? morphTarget : lerp(morphFromRef.current, morphTarget, eased);
        crossRef.current =
          progress >= 1 ? crossTarget : lerp(crossFromRef.current, crossTarget, eased);
      }

      viewRef.current = {
        x: Math.abs(viewTarget.x - view.x) < 0.05 ? viewTarget.x : lerp(view.x, viewTarget.x, 0.18),
        y: Math.abs(viewTarget.y - view.y) < 0.05 ? viewTarget.y : lerp(view.y, viewTarget.y, 0.18),
        scale:
          Math.abs(viewTarget.scale - view.scale) < 0.001
            ? viewTarget.scale
            : lerp(view.scale, viewTarget.scale, 0.18),
      };

      draw();

      const settled =
        morphRef.current === morphTarget &&
        crossRef.current === crossTarget &&
        viewRef.current.x === viewTarget.x &&
        viewRef.current.y === viewTarget.y &&
        viewRef.current.scale === viewTarget.scale;

      rafRef.current = settled ? 0 : requestAnimationFrame(step);
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
  }, [draw]);

  // Snap rather than tween when the reader asked for less motion.
  const commit = useCallback(() => {
    if (reducedRef.current) {
      morphRef.current = morphTargetRef.current;
      crossRef.current = crossTargetRef.current;
      viewRef.current = viewTargetRef.current;
      draw();
      return;
    }
    animate();
  }, [animate, draw]);

  const zoomBy = useCallback(
    (factor) => {
      const target = viewTargetRef.current;
      const next = { ...target, scale: clamp(target.scale * factor, MIN_SCALE, MAX_SCALE) };
      viewTargetRef.current = next;
      setMoved(hasMoved(next));
      commit();
    },
    [commit]
  );

  const recenter = useCallback(() => {
    viewTargetRef.current = IDENTITY;
    setMoved(false);
    commit();
  }, [commit]);

  const show = useCallback((next) => {
    if (next === "starred") setStarredArmed(true);
    setMode(next);
  }, []);

  useEffect(() => {
    reducedRef.current =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    layoutRef.current = layout;
    draw();
  }, [layout, draw]);

  // The sky arrives when its fetch resolves, which on a cold call is often
  // after the crossfade has already finished. Rewind and replay so the first
  // open -- the one time the bloom matters -- gets it instead of a snap.
  useEffect(() => {
    starLayoutRef.current = starLayout;
    if (starLayout && crossTargetRef.current === 1 && crossRef.current === 1) {
      crossFromRef.current = 0;
      crossRef.current = 0;
      morphFromRef.current = morphRef.current;
      tweenStartRef.current =
        typeof performance !== "undefined" ? performance.now() : Date.now();
    }
    commit();
  }, [starLayout, commit]);

  useEffect(() => {
    modeRef.current = mode;
    morphFromRef.current = morphRef.current;
    crossFromRef.current = crossRef.current;
    tweenStartRef.current =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    morphTargetRef.current = mode === "calendar" ? 1 : 0;
    crossTargetRef.current = mode === "starred" ? 1 : 0;
    if (mode === "calendar") {
      viewTargetRef.current = IDENTITY;
      setMoved(false);
    }
    setHovered(null);
    setHoveredRepo(null);
    commit();
  }, [mode, commit]);

  // React's onWheel is passive, so it cannot preventDefault. Attach by hand.
  useEffect(() => {
    const node = stageRef.current;
    if (!node) return undefined;

    const onWheel = (event) => {
      if (modeRef.current === "calendar") return;
      event.preventDefault();
      const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      zoomBy(clamp(Math.exp(-delta * 0.0015), 0.82, 1.18));
    };

    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [zoomBy]);

  // viewBox units per CSS pixel, honouring xMidYMid meet letterboxing.
  const unitsPerPixel = () => {
    const node = stageRef.current;
    if (!node) return 1;
    const rect = node.getBoundingClientRect();
    const scale = Math.min(rect.width / VIEW_W, rect.height / VIEW_H_GRAPH);
    return scale > 0 ? 1 / scale : 1;
  };

  const handlePointerDown = (event) => {
    if (modeRef.current === "calendar" || event.button !== 0) return;
    // Capture is deferred until the pointer has actually travelled, so a plain
    // click on a repo reaches its link instead of being eaten by the drag.
    dragRef.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      travel: 0,
      active: false,
    };
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;

    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    drag.travel += Math.abs(dx) + Math.abs(dy);
    if (!drag.active) {
      if (drag.travel < DRAG_SLOP) return;
      drag.active = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      setDragging(true);
    }

    const units = unitsPerPixel();
    const next = {
      ...viewRef.current,
      x: viewRef.current.x + dx * units,
      y: viewRef.current.y + dy * units,
    };
    drag.x = event.clientX;
    drag.y = event.clientY;
    viewRef.current = next;
    viewTargetRef.current = next;
    draw();
  };

  const handlePointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    if (drag.active && event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
    setDragging(false);
    setMoved(hasMoved(viewRef.current));
  };

  const handleOver = (event) => {
    const node = event.target;
    if (!node || !node.getAttribute) return;
    const repo = node.getAttribute("data-repo");
    if (repo != null) {
      setHoveredRepo(Number(repo));
      return;
    }
    const index = node.getAttribute("data-index");
    if (index != null) setHovered(Number(index));
  };

  const handleOut = (event) => {
    const node = event.target;
    if (!node || !node.getAttribute) return;
    if (node.getAttribute("data-repo") != null) setHoveredRepo(null);
    if (node.getAttribute("data-index") != null) setHovered(null);
  };

  // A drag that ends on a repo must not follow its link.
  const handleRepoClick = (event) => {
    const drag = dragRef.current;
    if (drag && drag.travel > DRAG_SLOP) event.preventDefault();
  };

  if (status === "error") {
    return (
      <div className="gh-card">
        <div className="gh-fallback">
          <span>Contribution data unavailable —</span>
          <a href={`https://github.com/${username}`} target="_blank" rel="noopener noreferrer">
            @{username} on GitHub
          </a>
        </div>
      </div>
    );
  }

  const isStarred = mode === "starred";
  const canPan = mode !== "calendar";
  const hoveredStar = hovered != null && layout ? layout.stars[hovered] : null;
  const hoveredEntry =
    hoveredRepo != null && starLayout ? starLayout.stars[hoveredRepo] : null;
  const activeCount = layout ? layout.active.length : 0;

  let pill;
  if (isStarred && starred.status === "error") {
    pill = "Starred repositories unavailable";
  } else if (isStarred && !starLayout) {
    pill = "Loading starred repositories…";
  } else if (isStarred && hoveredEntry) {
    pill = (
      <>
        <strong>{hoveredEntry.fullName}</strong> · {compactCount(hoveredEntry.stars)}{" "}
        {plural(hoveredEntry.stars, "star")}
        {hoveredEntry.language ? ` · ${hoveredEntry.language}` : ""}
      </>
    );
  } else if (isStarred) {
    pill = (
      <>
        <strong>{starLayout.total}</strong> {plural(starLayout.total, "repository")}{" "}
        starred, across <strong>{starLayout.languages}</strong>{" "}
        {plural(starLayout.languages, "language")}
      </>
    );
  } else if (status === "loading") {
    pill = "Loading contributions…";
  } else if (hoveredStar && hoveredStar.count > 0) {
    pill = (
      <>
        <strong>{hoveredStar.count.toLocaleString()}</strong>{" "}
        {plural(hoveredStar.count, "contribution")} on{" "}
        <strong>{formatDay(hoveredStar.date)}</strong>
      </>
    );
  } else if (hoveredStar) {
    pill = (
      <>
        No contributions on <strong>{formatDay(hoveredStar.date)}</strong>
      </>
    );
  } else {
    pill = (
      <>
        <strong>{total.toLocaleString()}</strong> {plural(total, "contribution")} in the
        last year
      </>
    );
  }

  return (
    <div className="gh-card" data-mode={mode}>
      <div className="gh-header">
        <a
          className="gh-link"
          href={`https://github.com/${username}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View @${username} on GitHub`}
        >
          {icons.github}
        </a>

        <div className="gh-toggle" role="group" aria-label="What the card shows">
          <button
            type="button"
            aria-label="Contributions as a constellation"
            aria-pressed={mode === "graph"}
            onClick={() => show("graph")}
          >
            {icons.spiral}
          </button>
          <button
            type="button"
            aria-label="Contributions as a calendar"
            aria-pressed={mode === "calendar"}
            onClick={() => show("calendar")}
          >
            {icons.calendar}
          </button>
          <span className="gh-toggle-rule" aria-hidden="true" />
          <button
            type="button"
            aria-label="Starred repositories"
            aria-pressed={isStarred}
            onClick={() => show("starred")}
          >
            {icons.star}
          </button>
        </div>
      </div>

      <button
        type="button"
        className={`gh-recenter${canPan && moved ? "" : " gh-controls-hidden"}`}
        aria-label="Recenter constellation"
        disabled={!(canPan && moved)}
        onClick={recenter}
      >
        {icons.recenter}
      </button>

      <div className={`gh-zoom${canPan ? "" : " gh-controls-hidden"}`}>
        <button type="button" aria-label="Zoom in" disabled={!canPan} onClick={() => zoomBy(1.16)}>
          {icons.plus}
        </button>
        <button type="button" aria-label="Zoom out" disabled={!canPan} onClick={() => zoomBy(0.86)}>
          {icons.minus}
        </button>
      </div>

      <div className="gh-pill">{pill}</div>

      <div
        className={`gh-stage${dragging ? " is-dragging" : ""}`}
        data-mode={mode}
        ref={stageRef}
        role="application"
        aria-label="Interactive GitHub contribution graph"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <svg
          className="gh-svg"
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H_GRAPH}`}
          preserveAspectRatio="xMidYMid meet"
          onPointerOver={handleOver}
          onPointerOut={handleOut}
          onFocus={handleOver}
          onBlur={handleOut}
        >
          <g ref={worldRef}>
            <path className="gh-arm" ref={armRef} d={ARM} />
            {layout && layout.linkPath ? (
              <path className="gh-links" ref={linksRef} d={layout.linkPath} />
            ) : null}

            <g ref={calLayerRef} opacity="0">
              {layout
                ? layout.rows.map((row) => (
                    <line
                      key={`rule-${row.key}`}
                      className="gh-row-rule"
                      x1={(CAL_GUTTER + CAL_INSET).toFixed(2)}
                      x2={(CAL_RIGHT - CAL_INSET).toFixed(2)}
                      y1={row.y.toFixed(2)}
                      y2={row.y.toFixed(2)}
                    />
                  ))
                : null}
              {layout
                ? layout.rows.map((row) => (
                    <text
                      key={`row-${row.key}`}
                      className="gh-cal-label"
                      x={CAL_LABEL_X}
                      y={(row.y + 3.5).toFixed(2)}
                      textAnchor="middle"
                    >
                      {row.label}
                    </text>
                  ))
                : null}
            </g>

            {layout ? (
              <g ref={daysLayerRef}>
                <g>
                  {layout.active.map((star, index) => (
                    <rect
                      key={`halo-${star.date}`}
                      className="gh-halo"
                      ref={(node) => {
                        haloRefs.current[index] = node;
                      }}
                      fill={STAR_COLORS[star.level]}
                      opacity="0"
                    />
                  ))}
                </g>

                <g ref={dustRef} fill={DUST_COLOR}>
                  {layout.stars
                    .filter((star) => !star.count)
                    .map((star) => (
                      <rect
                        key={star.date}
                        className="gh-star"
                        data-index={star.index}
                        ref={(node) => {
                          starRefs.current[star.index] = node;
                        }}
                      />
                    ))}
                </g>

                <g>
                  {layout.stars
                    .filter((star) => star.count > 0)
                    .map((star) => (
                      <rect
                        key={star.date}
                        className="gh-star"
                        data-index={star.index}
                        ref={(node) => {
                          starRefs.current[star.index] = node;
                        }}
                        fill={STAR_COLORS[star.level]}
                      />
                    ))}
                </g>

                <g>
                  {layout.active.map((star, index) => (
                    <rect
                      key={`hit-${star.date}`}
                      className="gh-hit"
                      data-index={star.index}
                      tabIndex={isStarred ? -1 : 0}
                      role="img"
                      aria-label={`${star.count} ${plural(star.count, "contribution")} on ${formatDay(star.date)}`}
                      ref={(node) => {
                        hitRefs.current[index] = node;
                      }}
                    />
                  ))}
                </g>
              </g>
            ) : null}

            <g ref={graphLabelsRef}>
              {layout
                ? layout.clusters.map((cluster) => (
                    <text
                      key={cluster.key}
                      className="gh-month-label"
                      x={cluster.x.toFixed(1)}
                      y={(cluster.y - 30).toFixed(1)}
                    >
                      {cluster.label}
                    </text>
                  ))
                : null}
            </g>

            {starLayout ? (
              <g className="gh-sky">
                <g>
                  {starLayout.anchors.map((repo, index) => (
                    <circle
                      key={`repo-halo-${repo.id}`}
                      className="gh-repo-halo"
                      ref={(node) => {
                        repoHaloRefs.current[index] = node;
                      }}
                      fill={REPO_COLORS[repo.level]}
                      opacity="0"
                    />
                  ))}
                </g>

                <g>
                  {starLayout.stars.map((repo, index) => (
                    <circle
                      key={`repo-${repo.id}`}
                      className="gh-repo"
                      ref={(node) => {
                        repoRefs.current[index] = node;
                      }}
                      fill={REPO_COLORS[repo.level]}
                      opacity="0"
                    />
                  ))}
                </g>

                <g>
                  {starLayout.clusters.map((cluster, index) => (
                    <text
                      key={`lang-${cluster.key}`}
                      className="gh-lang-label"
                      x={cluster.x.toFixed(1)}
                      y={cluster.labelY.toFixed(1)}
                      opacity="0"
                      ref={(node) => {
                        repoLabelRefs.current[index] = node;
                      }}
                    >
                      {cluster.label} · {cluster.count}
                    </text>
                  ))}
                </g>

                <g>
                  {starLayout.stars.map((repo, index) => (
                    <a
                      key={`repo-hit-${repo.id}`}
                      href={repo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      tabIndex={isStarred ? 0 : -1}
                      data-repo={index}
                      aria-label={`${repo.fullName}, ${repo.stars.toLocaleString()} ${plural(repo.stars, "star")}${repo.language ? `, ${repo.language}` : ""}`}
                      onClick={handleRepoClick}
                    >
                      <circle
                        className="gh-repo-hit"
                        data-repo={index}
                        ref={(node) => {
                          repoHitRefs.current[index] = node;
                        }}
                      />
                    </a>
                  ))}
                </g>
              </g>
            ) : null}
          </g>
        </svg>
      </div>

      <p className="gh-sr">
        {isStarred && starLayout ? (
          <>
            {starLayout.total} repositories starred on GitHub, grouped into{" "}
            {starLayout.languages} {plural(starLayout.languages, "language")}. Each
            repository is a link.
          </>
        ) : (
          <>
            {total.toLocaleString()} GitHub {plural(total, "contribution")} in the last
            year, across {activeCount} active {activeCount === 1 ? "day" : "days"}. Use
            the view toggle to switch between the constellation, the calendar, and the
            repositories Carlos has starred.
          </>
        )}
      </p>
    </div>
  );
};

export default GithubActivity;
